import { Controller, useFormContext } from 'react-hook-form';

import { EntityType } from '@polyai/stream/store/reducers/entities/types';
import type { OptionType } from '@polyai/ui/components/atoms/Select';
import Toggle from '@polyai/ui/components/atoms/Toggle';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import TagInput from '@polyai/ui/components/molecules/TagInput';

import { getCountryFlag } from 'utils/countryFlag';
import { COUNTRY_CODES, ISO_TO_PHONE_CODE } from '../constants';
import * as Styled from '../EntitySidebar.styled';
import { ENTITY_SIDEBAR_UI_COPY } from '../EntitySidebar.uiCopy';
import type { EntitySchemaProps } from '.';

interface Props {
  readonly: boolean;
}

const createCountryMapping = (): Map<string, string> => {
  const countryMapping = new Map<string, string>();
  const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

  COUNTRY_CODES.forEach(code => {
    try {
      const name = displayNames.of(code);
      if (name) {
        countryMapping.set(name.toLowerCase(), code);
      }
    } catch (error) {
      console.error('Error getting country name', error);
    }
  });

  return countryMapping;
};

// Cache the country mapping to avoid recreating it every time
let countryMapping: Map<string, string> | null = null;

export const getAllCountryOptions = (): OptionType[] => {
  if (!countryMapping) {
    countryMapping = createCountryMapping();
  }

  return Array.from(countryMapping.entries())
    .map(([countryName, isoCode]) => {
      const phoneCode = ISO_TO_PHONE_CODE[isoCode];
      if (!phoneCode) {
        return null;
      }

      const flag = getCountryFlag(isoCode);
      // Capitalize first letter of each word
      const formattedName = countryName
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      return {
        label: `${flag} ${phoneCode} ${formattedName}`,
        value: isoCode,
      };
    })
    .filter((option): option is OptionType => option !== null)
    .sort((a, b) => a.label.localeCompare(b.label));
};

const formatTags = (tags: string[], countryOptions: OptionType[]) => {
  if (!tags || tags.length === 0) {
    return [];
  }
  return tags.map(tag => {
    const option = countryOptions.find(o => o.value === tag);
    return option?.label || tag;
  });
};

// HACK: this is not good way of doing it, will refactor later
const getCountryCodesOfTags = (
  tags: string[],
  countryOptions: OptionType[],
) => {
  const res = tags.map(tag => {
    const option = countryOptions.find(o => o.value === tag || o.label === tag);
    return option?.value || tag;
  });

  return res;
};

export const PhoneNumberForm: React.FC<Props> = ({ readonly }) => {
  const { control, watch } = useFormContext<EntitySchemaProps>();

  const config = watch('config');

  const isPhoneNumberConfig = config?.$case === EntityType.PHONE_NUMBER;
  const isEnabled = isPhoneNumberConfig && config?.value.enabled;

  const countryOptions = getAllCountryOptions();

  return (
    <>
      <Controller
        control={control}
        name="config.value.enabled"
        render={({ field }) => (
          <Styled.SwitchWrapper>
            <Toggle handleToggle={field.onChange} toggleOn={!!field.value} />
            <Tooltip
              content={ENTITY_SIDEBAR_UI_COPY.form.phoneNumber.toggle.helpText}
            >
              <Styled.ToggleText>Set country code</Styled.ToggleText>
            </Tooltip>
          </Styled.SwitchWrapper>
        )}
      />

      {isEnabled && (
        <Controller
          key="config.value.countryCodes"
          control={control}
          name="config.value.countryCodes"
          render={({ field: { value, onChange } }) => {
            const availableOptions = countryOptions.filter(
              option => !value || !value.includes(option.value),
            );

            return (
              <TagInput
                disabled={false}
                options={availableOptions}
                placeholder="Type to search countries..."
                readonly={readonly}
                tags={formatTags(value, countryOptions)}
                onAdd={tags => {
                  onChange(getCountryCodesOfTags(tags, countryOptions));
                }}
                onDelete={tags => {
                  onChange(getCountryCodesOfTags(tags, countryOptions));
                }}
              />
            );
          }}
        />
      )}
    </>
  );
};
